import { Failure, Success } from "@pdfking/errors"
import { FillFlip, GetBestFill, fill_len } from "@pdfking/filling"
import type { ArrayForm, Validator } from "@pdfking/forms"
import type { ComponentFailure } from "@pdfking/imposition/internal"
import type { ComponentSchema, ImpositionSchema } from "@pdfking/imposition/internal"
import { LANDSCAPE } from "@pdfking/imposition/internal"
import { format_division_machine_size } from "@pdfking/imposition/internal"
import { ApplyComponentFailures } from "@pdfking/imposition/internal"
import { BuildComponentGrow } from "@pdfking/imposition/internal"
import { DefaultMachineSize } from "@pdfking/imposition/internal"
import { ApplyOrientation } from "@pdfking/imposition/internal"
import type { Distance, Rectangle } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"
import { RecordMap } from "@pdfking/tfunc"

/** Which jobs, component by component, were given no machine size. */
type Blank = boolean[][]

const CollectSizeErrors = (input: ImpositionSchema, blank: Blank): ComponentFailure[] => {
  const values: ComponentFailure[] = []

  // The machine size is settled here, once, for everything after: an entered
  // one is turned landscape, and a job left blank gets the smallest sheet its
  // own content stands on, the way the content stands.
  input.component_array.forEach((component, ci) => {
    const jobs = component.job_array
    if (jobs.length === 0) return

    const print_front = jobs.some(j => Boolean(j.job_destination) && j.print_mode.is_front)
    const print_rear = jobs.some(j => Boolean(j.job_destination) && j.print_mode.is_rear)

    let size: Rectangle<Distance> = BuildComponentGrow(
      input,
      component,
      print_front,
      print_rear
    ).size

    let machine_size = size

    for (let ji = 0; ji < jobs.length; ji++) {
      const job = jobs[ji]!
      const push = (
        path: (string | number)[],
        error: ReturnType<typeof Success> | ReturnType<typeof Failure>
      ) => values.push({ path: [ci, "job_array", ji, ...path], error })

      const fill_opts = {
        padding: ji === 0 ? component.padding : ZERO_MARGINS,
        security: job.margins,
        flip: ji === 0 ? input.flip.id : FillFlip.FLIP_EFFICIENT,
        use_symmetry: job.print_mode.use_symmetry,
      }

      // `machine_size` is still the previous job's: its fill stood back up.
      machine_size = blank[ci]![ji]
        ? DefaultMachineSize(
            ji === 0 ? size : machine_size,
            fill_opts.padding,
            fill_opts.security,
            fill_opts.use_symmetry
          )
        : ApplyOrientation(job.machine_size, LANDSCAPE)

      if (machine_size !== job.machine_size) push(["machine_size"], Success(machine_size))

      const fill = GetBestFill(machine_size, size, fill_opts)

      if (fill_len(fill) === 0) {
        const job_size = size
        push(
          ["machine_size"],
          Failure("SIZE_TOO_LARGE_FOR_MACHINE", {
            input: job_size,
            size: `${format_division_machine_size(job_size)} cm`,
            machine: `${format_division_machine_size(machine_size)} cm`,
          })
        )
        return
      }

      size = { width: fill.width, height: fill.height }
    }
  })

  return values
}

/**
 * Top-level form validator: a job whose content can't be packed into its
 * `machine_size` fails that field with `SIZE_TOO_LARGE_FOR_MACHINE` — mirrors
 * Python `_get_imposition_data` (`packing/presets/concrete.py:299-313`), which
 * `form.add_error`s the same path. The fit verdict comes from
 * {@link CollectSizeErrors}; the error is placed on the exact nested node.
 *
 * It is also what makes `machine_size` a field every job has: the field is
 * required, and one left blank is given its size here instead of its error.
 */
export const ValidateImpositionSizes: Validator<ImpositionSchema> = results => {
  if (results.size.error || results.orientation_kind.error) return results

  const components = (results.component_array as ArrayForm<ComponentSchema>).results
  const blank: Blank = components.map(component =>
    component.results.job_array.results.map(
      job => job.results.machine_size.error?.enum === "REQUIRED_ERROR"
    )
  )
  const cleared = ApplyComponentFailures(
    results,
    blank.flatMap((jobs, ci) =>
      jobs.flatMap((is_blank, ji) =>
        is_blank ? [{ path: [ci, "job_array", ji, "machine_size"], error: Success(undefined) }] : []
      )
    )
  )

  // structurally invalid input → fit isn't meaningful yet, leave existing errors
  if (cleared.component_array.error) return results

  const input = RecordMap(child => child.value, cleared) as ImpositionSchema
  return ApplyComponentFailures(cleared, CollectSizeErrors(input, blank))
}
